import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Like (follow) button for a farmer profile.
 *
 * Props:
 *   farmerId       (string)
 *   initialFollowed(bool)
 *   variant        'icon' | 'pill' (default 'pill')
 *   onChange       (followed: bool) => void
 *   className      extra wrapper classes
 *   testId         (string)
 */
export default function LikeFarmerButton({
  farmerId,
  initialFollowed = false,
  variant = 'pill',
  onChange,
  className = '',
  testId,
}) {
  const { api, user } = useAuth();
  const [followed, setFollowed] = useState(initialFollowed);
  const [busy, setBusy] = useState(false);

  // Sync if parent updates the initial value (e.g., after profile fetch)
  React.useEffect(() => {
    setFollowed(initialFollowed);
  }, [initialFollowed]);

  const isSelf = user && user._id === farmerId;
  if (isSelf) return null;

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/farmer/${farmerId}/follow`);
      setFollowed(data.followed);
      onChange?.(data.followed);
      toast.success(data.followed ? 'Added to liked farmers' : 'Removed from liked');
    } catch {
      toast.error('Could not update — please try again');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={toggle}
        disabled={busy}
        className={`w-9 h-9 rounded-full bg-[#F6F8FB]/90 backdrop-blur-sm flex items-center justify-center border border-[#E4E8EF] active:scale-90 transition-transform ${className}`}
        aria-pressed={followed}
        aria-label={followed ? 'Unlike farmer' : 'Like farmer'}
      >
        <Heart
          className={`w-4 h-4 transition-colors ${
            followed ? 'fill-[#B91C1C] text-[#B91C1C]' : 'text-[#5B6675]'
          }`}
        />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={toggle}
      disabled={busy}
      aria-pressed={followed}
      className={`inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold transition-colors disabled:opacity-60 ${
        followed
          ? 'bg-[#B91C1C]/10 text-[#B91C1C] border border-[#B91C1C]/20 hover:bg-[#B91C1C]/15'
          : 'bg-[#0B4F3F] text-[#F6F8FB] hover:bg-[#083D31]'
      } ${className}`}
    >
      <Heart
        className={`w-3.5 h-3.5 ${followed ? 'fill-[#B91C1C]' : ''}`}
      />
      {followed ? 'Liked' : 'Like'}
    </button>
  );
}
